import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router'
import { z } from 'zod'
import Wizard from '@cloudscape-design/components/wizard'
import type { WizardProps } from '@cloudscape-design/components/wizard'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Select from '@cloudscape-design/components/select'
import Multiselect from '@cloudscape-design/components/multiselect'
import type { MultiselectProps } from '@cloudscape-design/components/multiselect'
import type { SelectProps } from '@cloudscape-design/components/select'
import Textarea from '@cloudscape-design/components/textarea'
import Checkbox from '@cloudscape-design/components/checkbox'
import Toggle from '@cloudscape-design/components/toggle'
import TokenGroup from '@cloudscape-design/components/token-group'
import SpaceBetween from '@cloudscape-design/components/space-between'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import Spinner from '@cloudscape-design/components/spinner'
import Flashbar from '@cloudscape-design/components/flashbar'
import { useClient, useUpdateClient, useExtractWebsiteFields, useRecommendCampaignSettings } from '@/hooks/useClients'
import { useNotificationStore } from '@/state/notificationStore'
import type { UpdateClientInput } from '@/hooks/useClients'
import FillFromWebsiteModal from './FillFromWebsiteModal'
import type { ExtractableFields } from './FillFromWebsiteModal'
import type { WebsiteSuggestions } from '@/lib/types'
import {
  useSearchGooglePlaces,
  useSetClientPlaceId,
  useClientGbpPlace,
  useRemoveClientPlaceId,
} from '@/hooks/useGbpConfig'
import { useGbpReviews } from '@/hooks/useGbpReviews'
import {
  wizardStep1Schema,
  wizardStep2Schema,
  wizardStep3Schema,
  wizardStep4Schema,
  wizardStep5Schema,
} from '@/lib/schemas'
import { ALL_CONTENT_TYPES } from '@/lib/types'
import type { ScheduleCadence } from '@/lib/types'

// ─── Constants ───────────────────────────────────────────────────────────────

const INDUSTRY_OPTIONS = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'general', label: 'General' },
  { value: 'legal', label: 'Legal' },
  { value: 'financial', label: 'Financial' },
  { value: 'food_service', label: 'Food Service' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'education', label: 'Education' },
  { value: 'technology', label: 'Technology' },
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'construction', label: 'Construction' },
  { value: 'beauty_wellness', label: 'Beauty & Wellness' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'travel_hospitality', label: 'Travel & Hospitality' },
  { value: 'home_services', label: 'Home Services' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'media_entertainment', label: 'Media & Entertainment' },
]

const CONTENT_TYPE_LABELS: Record<string, string> = {
  facebook_post: 'Facebook Post',
  twitter_post: 'Twitter/X Post',
  instagram_post: 'Instagram Post',
  linkedin_post: 'LinkedIn Post',
  blog_seo: 'Blog (SEO)',
  email_outreach: 'Email Outreach',
  facebook_ad: 'Facebook Ad',
  instagram_ad: 'Instagram Ad',
  google_ads: 'Google Ads',
  gbp_post: 'Google Business Post',
}

// Auto-generate is limited to social posts (facebook + linkedin) — matches
// the spec of the legacy ScheduleAutoGenerateSection that this step replaces.
const SCHEDULE_CONTENT_TYPE_OPTIONS: MultiselectProps.Option[] = [
  { label: 'Facebook Post', value: 'facebook_post' },
  { label: 'LinkedIn Post', value: 'linkedin_post' },
]

const CADENCE_OPTIONS: SelectProps.Option[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Custom (cron)', value: 'custom_cron' },
]

const DAY_OPTIONS: MultiselectProps.Option[] = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
]

// ─── Combined schema ─────────────────────────────────────────────────────────

const wizardSchema = wizardStep1Schema
  .merge(wizardStep2Schema)
  .merge(wizardStep3Schema)
  .merge(wizardStep4Schema)
  .merge(wizardStep5Schema)
  .superRefine((v, ctx) => {
    // Schedule cross-field rules only apply when the user has enabled auto-gen.
    if (!v.scheduleEnabled) return
    if (!v.scheduleContentTypes || v.scheduleContentTypes.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduleContentTypes'],
        message: 'Pick at least one content type',
      })
    }
    if (!v.scheduleTimeUtc || !/^([01]\d|2[0-3]):[0-5]\d$/.test(v.scheduleTimeUtc)) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduleTimeUtc'],
        message: 'Must be HH:MM (24-hour)',
      })
    }
    if (v.scheduleCadence === 'weekly' && (!v.scheduleDaysOfWeek || v.scheduleDaysOfWeek.length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduleDaysOfWeek'],
        message: 'Pick at least one day',
      })
    }
    if (v.scheduleCadence === 'custom_cron' && (!v.scheduleCronExpr || v.scheduleCronExpr.trim().length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduleCronExpr'],
        message: 'Cron expression is required',
      })
    }
  })

type WizardFormData = z.infer<typeof wizardSchema>

// ─── TagInput helper ─────────────────────────────────────────────────────────

interface TagInputProps {
  value: string[]
  onChange: (v: string[]) => void
}

function TagInput({ value, onChange }: TagInputProps) {
  const [input, setInput] = useState('')
  const addTag = () => {
    const trimmed = input.trim()
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed])
    }
    setInput('')
  }
  return (
    <SpaceBetween size="xs">
      <SpaceBetween direction="horizontal" size="xs">
        <Input
          value={input}
          onChange={({ detail }) => setInput(detail.value)}
          onKeyDown={({ detail }) => {
            if (detail.key === 'Enter') addTag()
          }}
          placeholder="Type and press Enter to add"
        />
        <Button onClick={addTag} iconName="add-plus">
          Add
        </Button>
      </SpaceBetween>
      {value.length > 0 && (
        <TokenGroup
          items={value.map(v => ({ label: v, dismissLabel: `Remove ${v}` }))}
          onDismiss={({ detail: { itemIndex } }) =>
            onChange(value.filter((_, i) => i !== itemIndex))
          }
        />
      )}
    </SpaceBetween>
  )
}

// ─── Field groups per step ───────────────────────────────────────────────────

const STEP_FIELDS: Array<Array<keyof WizardFormData>> = [
  ['brandName', 'brandSpecialization', 'industry', 'websiteUrl', 'brandLocation', 'phone', 'serviceArea'],
  ['facebookUrl', 'linkedinUrl', 'twitterUrl', 'ctaUrl'],
  ['businessDescription', 'productCategories', 'servicesOffered', 'deliveryTimeline', 'acceptedInsurance', 'ehrIntegrations'],
  ['campaignGoal', 'selectedContentTypes', 'budgetLimitDailyUsd', 'includeAddress', 'includePhone', 'includeEmail', 'includeWebsite', 'includePricing', 'includeStaffNames'],
  ['scheduleEnabled', 'scheduleContentTypes', 'scheduleCadence', 'scheduleTimeUtc', 'scheduleDaysOfWeek', 'scheduleCronExpr', 'scheduleTimezone', 'scheduleNotifyOnQueue'],
]

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  clientId: string
  /**
   * Zero-indexed step to open on. When omitted, starts at step 0.
   */
  startStep?: number
  /**
   * Called after a successful submit. When provided, overrides the default
   * behavior of navigating to the client overview — the parent decides what
   * to do (e.g. return to a summary view).
   */
  onDone?: () => void
  /**
   * Called when the user cancels out of the wizard. When provided, overrides
   * the default navigate-to-overview behavior.
   */
  onCancel?: () => void
}

function getBrowserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof tz === 'string' && tz.length > 0 ? tz : ''
  } catch {
    return ''
  }
}

export default function ConfigureWizard({ clientId, startStep = 0, onDone, onCancel }: Props) {
  const navigate = useNavigate()
  const { client, loading: clientLoading } = useClient(clientId)
  const { updateClient } = useUpdateClient()

  // GBP search state (not part of react-hook-form — it's a lookup)
  const [gbpSearchQuery, setGbpSearchQuery] = useState('')
  const [gbpConfirmed, setGbpConfirmed] = useState<string | null>(null)

  const { searchPlaces, results: gbpResults, loading: gbpSearching, called: gbpCalled } =
    useSearchGooglePlaces()
  const { setPlaceId, loading: gbpSaving } = useSetClientPlaceId()
  const { place: currentPlace, loading: currentPlaceLoading } = useClientGbpPlace(clientId)
  const { removePlaceId, loading: removingPlace } = useRemoveClientPlaceId()
  const { reviews: gbpReviews, loading: gbpReviewsLoading } = useGbpReviews(clientId)

  // Wizard state
  const [activeStep, setActiveStep] = useState(startStep)
  const [saving, setSaving] = useState(false)

  // Website field extraction
  const [websiteSuggestions, setWebsiteSuggestions] = useState<WebsiteSuggestions | null>(null)
  const [extractError, setExtractError] = useState<string | null>(null)
  const { extract, loading: extracting } = useExtractWebsiteFields()

  // AI campaign recommendations
  const [recommendError, setRecommendError] = useState<string | null>(null)
  const { recommend, loading: recommending } = useRecommendCampaignSettings()

  // Pull camelCase fields from GraphQL response
  const marketingProfile = client?.marketingProfile
  const contentInclusion = client?.contentInclusion
  const capabilities = client?.capabilities
  const schedulingConfig = client?.schedulingConfig
  const autoGenerate = schedulingConfig?.autoGenerate

  // Only social-post content types are allowed in the schedule step.
  const scheduleAllowed = SCHEDULE_CONTENT_TYPE_OPTIONS
    .map(o => o.value)
    .filter((v): v is string => typeof v === 'string')
  const scheduleContentTypeDefault = (autoGenerate?.contentTypes ?? []).filter(
    (t): t is 'facebook_post' | 'linkedin_post' =>
      scheduleAllowed.includes(t),
  )

  const form = useForm<WizardFormData>({
    resolver: zodResolver(wizardSchema),
    defaultValues: {
      brandName: client?.brandName ?? client?.brand_name ?? '',
      brandSpecialization: client?.brandSpecialization ?? client?.brand_specialization ?? '',
      industry: client?.industry ?? '',
      websiteUrl: client?.websiteUrl ?? client?.website_url ?? '',
      brandLocation: client?.brandLocation ?? client?.brand_location ?? '',
      phone: marketingProfile?.phone ?? '',
      serviceArea: capabilities?.serviceArea ?? '',
      facebookUrl: marketingProfile?.facebookUrl ?? '',
      linkedinUrl: marketingProfile?.linkedinUrl ?? '',
      twitterUrl: marketingProfile?.twitterUrl ?? '',
      ctaUrl: marketingProfile?.ctaUrl ?? '',
      businessDescription: capabilities?.businessDescription ?? '',
      productCategories: capabilities?.productCategories ?? [],
      servicesOffered: capabilities?.servicesOffered ?? [],
      deliveryTimeline: capabilities?.deliveryTimeline ?? '',
      acceptedInsurance: capabilities?.acceptedInsurance ?? [],
      ehrIntegrations: capabilities?.ehrIntegrations ?? [],
      campaignGoal: marketingProfile?.campaignGoal ?? '',
      selectedContentTypes: client?.selectedContentTypes ?? client?.selected_content_types ?? [],
      budgetLimitDailyUsd: client?.budgetLimitDailyUsd ?? client?.budget_limit_daily_usd ?? undefined,
      includeAddress: contentInclusion?.includeAddress ?? false,
      includePhone: contentInclusion?.includePhone ?? false,
      includeEmail: contentInclusion?.includeEmail ?? false,
      includeWebsite: contentInclusion?.includeWebsite ?? false,
      includePricing: contentInclusion?.includePricing ?? false,
      includeStaffNames: contentInclusion?.includeStaffNames ?? false,
      scheduleEnabled: autoGenerate?.enabled ?? false,
      scheduleContentTypes: scheduleContentTypeDefault,
      scheduleCadence: autoGenerate?.cadence ?? 'daily',
      scheduleTimeUtc: autoGenerate?.timeUtc ?? '09:00',
      scheduleDaysOfWeek: autoGenerate?.daysOfWeek ?? [],
      scheduleCronExpr: autoGenerate?.cronExpr ?? '',
      scheduleTimezone: autoGenerate?.timezone ?? getBrowserTimezone(),
      scheduleNotifyOnQueue: schedulingConfig?.notifyOnQueue ?? false,
    },
    values: client
      ? {
          brandName: client.brandName ?? client.brand_name ?? '',
          brandSpecialization: client.brandSpecialization ?? client.brand_specialization ?? '',
          industry: client.industry ?? '',
          websiteUrl: client.websiteUrl ?? client.website_url ?? '',
          brandLocation: client.brandLocation ?? client.brand_location ?? '',
          phone: marketingProfile?.phone ?? '',
          serviceArea: capabilities?.serviceArea ?? '',
          facebookUrl: marketingProfile?.facebookUrl ?? '',
          linkedinUrl: marketingProfile?.linkedinUrl ?? '',
          twitterUrl: marketingProfile?.twitterUrl ?? '',
          ctaUrl: marketingProfile?.ctaUrl ?? '',
          businessDescription: capabilities?.businessDescription ?? '',
          productCategories: capabilities?.productCategories ?? [],
          servicesOffered: capabilities?.servicesOffered ?? [],
          deliveryTimeline: capabilities?.deliveryTimeline ?? '',
          acceptedInsurance: capabilities?.acceptedInsurance ?? [],
          ehrIntegrations: capabilities?.ehrIntegrations ?? [],
          campaignGoal: marketingProfile?.campaignGoal ?? '',
          selectedContentTypes: client.selectedContentTypes ?? client.selected_content_types ?? [],
          budgetLimitDailyUsd: client.budgetLimitDailyUsd ?? client.budget_limit_daily_usd ?? undefined,
          includeAddress: contentInclusion?.includeAddress ?? false,
          includePhone: contentInclusion?.includePhone ?? false,
          includeEmail: contentInclusion?.includeEmail ?? false,
          includeWebsite: contentInclusion?.includeWebsite ?? false,
          includePricing: contentInclusion?.includePricing ?? false,
          includeStaffNames: contentInclusion?.includeStaffNames ?? false,
          scheduleEnabled: autoGenerate?.enabled ?? false,
          scheduleContentTypes: scheduleContentTypeDefault,
          scheduleCadence: autoGenerate?.cadence ?? 'daily',
          scheduleTimeUtc: autoGenerate?.timeUtc ?? '09:00',
          scheduleDaysOfWeek: autoGenerate?.daysOfWeek ?? [],
          scheduleCronExpr: autoGenerate?.cronExpr ?? '',
          scheduleTimezone: autoGenerate?.timezone ?? getBrowserTimezone(),
          scheduleNotifyOnQueue: schedulingConfig?.notifyOnQueue ?? false,
        }
      : undefined,
    resetOptions: { keepDirtyValues: true },
  })

  const { control, trigger, getValues, watch, setValue, formState: { errors } } = form
  const selectedContentTypes = watch('selectedContentTypes') ?? []
  const websiteUrlValue = watch('websiteUrl')

  // ─── Save helpers ──────────────────────────────────────────────────────────

  const pushError = (message: string) => {
    useNotificationStore.getState().add({ type: 'error', content: message })
  }

  const buildStepPayload = (stepIndex: number): UpdateClientInput => {
    const v = getValues()
    if (stepIndex === 0) {
      return {
        brandName: v.brandName,
        brandSpecialization: v.brandSpecialization,
        industry: v.industry,
        websiteUrl: v.websiteUrl,
        brandLocation: v.brandLocation,
        marketingProfile: { phone: v.phone },
        capabilities: { serviceArea: v.serviceArea },
      }
    }
    if (stepIndex === 1) {
      return {
        marketingProfile: {
          facebookUrl: v.facebookUrl,
          linkedinUrl: v.linkedinUrl,
          twitterUrl: v.twitterUrl,
          ctaUrl: v.ctaUrl,
          ...(gbpReviews != null && {
            gbpReviewCount: gbpReviews.totalReviews,
            gbpRating: gbpReviews.averageRating,
          }),
        },
      }
    }
    if (stepIndex === 2) {
      return {
        capabilities: {
          businessDescription: v.businessDescription,
          productCategories: v.productCategories,
          servicesOffered: v.servicesOffered,
          deliveryTimeline: v.deliveryTimeline,
          acceptedInsurance: v.acceptedInsurance,
          ehrIntegrations: v.ehrIntegrations,
        },
      }
    }
    if (stepIndex === 3) {
      return {
        marketingProfile: { campaignGoal: v.campaignGoal },
        selectedContentTypes: v.selectedContentTypes,
        budgetLimitDailyUsd: v.budgetLimitDailyUsd,
        contentInclusion: {
          includeAddress: v.includeAddress,
          includePhone: v.includePhone,
          includeEmail: v.includeEmail,
          includeWebsite: v.includeWebsite,
          includePricing: v.includePricing,
          includeStaffNames: v.includeStaffNames,
        },
      }
    }
    // Step 4: Auto-generation schedule
    return {
      schedulingConfig: {
        autoGenerate: {
          enabled: v.scheduleEnabled ?? false,
          contentTypes: v.scheduleContentTypes ?? [],
          cadence: v.scheduleCadence ?? 'daily',
          timeUtc: v.scheduleTimeUtc ?? '09:00',
          daysOfWeek: v.scheduleDaysOfWeek ?? [],
          cronExpr: v.scheduleCronExpr ?? '',
          timezone: v.scheduleTimezone ?? '',
        },
        notifyOnQueue: v.scheduleNotifyOnQueue ?? false,
      },
    }
  }

  const saveCurrentStep = async (stepIndex: number): Promise<boolean> => {
    const fields = STEP_FIELDS[stepIndex]
    const valid = await trigger(fields)
    if (!valid) return false
    setSaving(true)
    try {
      await updateClient(clientId, buildStepPayload(stepIndex))
      return true
    } catch (err) {
      pushError(err instanceof Error ? err.message : 'Failed to save changes.')
      return false
    } finally {
      setSaving(false)
    }
  }

  const saveAndNavigate = async (requestedStepIndex: number, reason: string): Promise<void> => {
    if (reason === 'next' || reason === 'skip') {
      const ok = await saveCurrentStep(activeStep)
      if (ok) setActiveStep(requestedStepIndex)
    } else {
      setActiveStep(requestedStepIndex)
    }
  }

  const handleNavigate: WizardProps['onNavigate'] = (event) => {
    const { requestedStepIndex, reason } = event.detail
    void saveAndNavigate(requestedStepIndex, reason)
  }

  const saveAndSubmit = async (): Promise<void> => {
    const ok = await saveCurrentStep(activeStep)
    if (ok) {
      useNotificationStore.getState().add({ type: 'success', content: 'Configuration saved successfully.' })
      if (onDone) {
        onDone()
      } else {
        navigate(`/clients/${clientId}`)
      }
    }
  }

  const handleSubmit: WizardProps['onSubmit'] = () => {
    void saveAndSubmit()
  }

  const handleCancel: WizardProps['onCancel'] = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate(`/clients/${clientId}`)
    }
  }

  // ─── Website extraction handlers ──────────────────────────────────────────

  const handleFillFromWebsite = async () => {
    setExtractError(null)
    const url = getValues('websiteUrl')
    if (!url) return
    try {
      const result = await extract(clientId, url)
      const s = result?.data?.extractWebsiteFields ?? {}
      if (!Object.values(s).some(v => v != null)) {
        setExtractError('No data could be extracted from this URL.')
        return
      }
      setWebsiteSuggestions(s)
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : 'Failed to extract website data.')
    }
  }

  const handleApplySuggestions = (selected: ExtractableFields) => {
    for (const [key, value] of Object.entries(selected)) {
      setValue(key as keyof WizardFormData, value as WizardFormData[keyof WizardFormData], { shouldDirty: true })
    }
    setWebsiteSuggestions(null)
  }

  const handleRecommendSettings = async () => {
    if (!clientId) return
    setRecommendError(null)
    try {
      const result = await recommend(clientId)
      const r = result?.data?.recommendCampaignSettings
      if (!r) return
      if (r.campaignGoal) setValue('campaignGoal', r.campaignGoal, { shouldDirty: true })
      if (r.selectedContentTypes?.length) setValue('selectedContentTypes', r.selectedContentTypes, { shouldDirty: true })
      if (r.scheduleCadence) setValue('scheduleCadence', r.scheduleCadence as 'daily' | 'weekly' | 'custom_cron', { shouldDirty: true })
      if (r.scheduleTimeUtc) setValue('scheduleTimeUtc', r.scheduleTimeUtc, { shouldDirty: true })
      if (r.scheduleDaysOfWeek?.length) setValue('scheduleDaysOfWeek', r.scheduleDaysOfWeek, { shouldDirty: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate recommendations.'
      setRecommendError(msg.replace('LLM_ERROR: ', ''))
    }
  }

  // ─── GBP handlers ──────────────────────────────────────────────────────────

  const handleGbpSearch = () => {
    if (!clientId || !gbpSearchQuery.trim()) return
    searchPlaces(clientId, gbpSearchQuery.trim())
  }

  const handleGbpConfirm = async (placeId: string, name: string) => {
    if (!clientId) return
    const result = await setPlaceId(clientId, placeId)
    if (result?.data?.setClientPlaceId) {
      setGbpConfirmed(name)
    }
  }

  const handleGbpRemove = async () => {
    if (!clientId) return
    try {
      await removePlaceId(clientId)
    } catch {
      pushError('Failed to remove business profile.')
    }
  }

  // ─── Loading state ─────────────────────────────────────────────────────────

  if (clientLoading && !client) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    )
  }

  // ─── Step contents ─────────────────────────────────────────────────────────

  const step1Content = (
    <>
      {websiteSuggestions && (
        <FillFromWebsiteModal
          suggestions={websiteSuggestions}
          currentValues={{
            brandName: getValues('brandName') ?? undefined,
            brandLocation: getValues('brandLocation') ?? undefined,
            brandSpecialization: getValues('brandSpecialization') ?? undefined,
            industry: getValues('industry') ?? undefined,
            phone: getValues('phone') ?? undefined,
            businessDescription: getValues('businessDescription') ?? undefined,
            servicesOffered: getValues('servicesOffered') ?? undefined,
            productCategories: getValues('productCategories') ?? undefined,
            serviceArea: getValues('serviceArea') ?? undefined,
            facebookUrl: getValues('facebookUrl') ?? undefined,
            linkedinUrl: getValues('linkedinUrl') ?? undefined,
            twitterUrl: getValues('twitterUrl') ?? undefined,
            ctaUrl: getValues('ctaUrl') ?? undefined,
          }}
          websiteUrl={getValues('websiteUrl') ?? ''}
          onApply={handleApplySuggestions}
          onDismiss={() => setWebsiteSuggestions(null)}
        />
      )}
      {extractError && (
        <Flashbar
          items={[{
            type: 'error',
            content: extractError,
            dismissible: true,
            onDismiss: () => setExtractError(null),
          }]}
        />
      )}
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                iconName="search"
                loading={extracting}
                disabled={!websiteUrlValue || !!errors.websiteUrl || extracting}
                onClick={() => { void handleFillFromWebsite() }}
              >
                Fill from website
              </Button>
            }
          >
            Business Info
          </Header>
        }
      >
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
          <Controller
            control={control}
            name="brandName"
            render={({ field }) => (
              <FormField label="Brand name" errorText={errors.brandName?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="Acme Corp"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="brandSpecialization"
            render={({ field }) => (
              <FormField label="Brand specialization" errorText={errors.brandSpecialization?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="Medical equipment, law firm, etc."
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="industry"
            render={({ field }) => {
              const selectedOption = INDUSTRY_OPTIONS.find(o => o.value === field.value) ?? null
              return (
                <FormField label="Industry" errorText={errors.industry?.message}>
                  <Select
                    selectedOption={selectedOption}
                    options={INDUSTRY_OPTIONS}
                    onChange={({ detail }) => field.onChange(detail.selectedOption.value ?? '')}
                    placeholder="Choose an industry"
                  />
                </FormField>
              )
            }}
          />
          <Controller
            control={control}
            name="websiteUrl"
            render={({ field }) => (
              <FormField label="Website URL" errorText={errors.websiteUrl?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="https://example.com"
                  type="url"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="brandLocation"
            render={({ field }) => (
              <FormField label="Brand location" errorText={errors.brandLocation?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="Minneapolis, MN"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <FormField label="Phone" errorText={errors.phone?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="(555) 555-5555"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="serviceArea"
            render={({ field }) => (
              <FormField label="Service area" errorText={errors.serviceArea?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="Twin Cities metro area"
                />
              </FormField>
            )}
          />
        </ColumnLayout>
      </SpaceBetween>
      </Container>
    </>
  )

  const step2Content = (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Google Business Profile</Header>}>
        <SpaceBetween size="m">
          {gbpConfirmed && (
            <Flashbar
              items={[
                {
                  id: 'gbp-confirmed',
                  type: 'success',
                  dismissible: true,
                  onDismiss: () => setGbpConfirmed(null),
                  content: `Google Business Profile set to "${gbpConfirmed}".`,
                },
              ]}
            />
          )}

          {currentPlaceLoading && <Spinner />}

          {currentPlace && !currentPlaceLoading && (
            <SpaceBetween size="s">
              <Box variant="awsui-key-label">Current business profile</Box>
              <Box>{currentPlace.name}</Box>
              <Box color="text-body-secondary">{currentPlace.address}</Box>
              {gbpReviewsLoading && <Spinner />}
              {gbpReviews && !gbpReviewsLoading && (
                <Box color="text-body-secondary">
                  {gbpReviews.averageRating} ★ average · {gbpReviews.totalReviews} reviews
                  <Box variant="span" color="text-status-inactive"> (synced from Google)</Box>
                </Box>
              )}
              <Button onClick={handleGbpRemove} loading={removingPlace}>
                Remove business profile
              </Button>
            </SpaceBetween>
          )}

          {!currentPlace && !currentPlaceLoading && (
            <FormField
              label="Search for your business"
              description="Enter your business name and location to link it to Google Maps."
            >
              <SpaceBetween size="xs" direction="horizontal">
                <Input
                  value={gbpSearchQuery}
                  placeholder="e.g. Jama Medical Equipment Minneapolis"
                  onChange={({ detail }) => setGbpSearchQuery(detail.value)}
                  onKeyDown={({ detail }) => {
                    if (detail.key === 'Enter') handleGbpSearch()
                  }}
                />
                <Button
                  variant="primary"
                  onClick={handleGbpSearch}
                  loading={gbpSearching}
                  disabled={!gbpSearchQuery.trim()}
                >
                  Search Google Maps
                </Button>
              </SpaceBetween>
            </FormField>
          )}

          {gbpSearching && <Spinner />}

          {gbpCalled && !gbpSearching && gbpResults.length === 0 && !currentPlace && (
            <Box color="text-status-inactive">
              No listings found. Try a different search.
            </Box>
          )}

          {gbpResults.length > 0 && !currentPlace && (
            <Cards
              cardDefinition={{
                header: item => item.name,
                sections: [
                  { id: 'address', header: 'Address', content: item => item.address },
                  {
                    id: 'rating',
                    header: 'Rating',
                    content: item =>
                      item.rating != null
                        ? `${item.rating} (${item.totalRatings ?? 0} reviews)`
                        : 'No rating yet',
                  },
                  {
                    id: 'action',
                    content: item => (
                      <Button
                        variant="primary"
                        loading={gbpSaving}
                        onClick={() => handleGbpConfirm(item.placeId, item.name)}
                      >
                        Use this listing
                      </Button>
                    ),
                  },
                ],
              }}
              items={gbpResults}
              cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
            />
          )}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Social profiles</Header>}>
        <ColumnLayout columns={2}>
          <Controller
            control={control}
            name="facebookUrl"
            render={({ field }) => (
              <FormField label="Facebook URL" errorText={errors.facebookUrl?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="https://facebook.com/yourpage"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="linkedinUrl"
            render={({ field }) => (
              <FormField label="LinkedIn URL" errorText={errors.linkedinUrl?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="https://linkedin.com/company/your-company"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="twitterUrl"
            render={({ field }) => (
              <FormField label="Twitter/X URL" errorText={errors.twitterUrl?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="https://x.com/yourhandle"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="ctaUrl"
            render={({ field }) => (
              <FormField label="Call-to-action URL" errorText={errors.ctaUrl?.message}>
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="https://example.com/contact"
                />
              </FormField>
            )}
          />
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  )

  const step3Content = (
    <Container header={<Header variant="h2">Services and insurance</Header>}>
      <SpaceBetween size="l">
        <Controller
          control={control}
          name="businessDescription"
          render={({ field }) => (
            <FormField
              label="Business description"
              description="A short description of your business used to seed content."
              errorText={errors.businessDescription?.message}
            >
              <Textarea
                value={field.value ?? ''}
                onChange={({ detail }) => field.onChange(detail.value)}
                rows={4}
              />
            </FormField>
          )}
        />
        <Controller
          control={control}
          name="productCategories"
          render={({ field }) => (
            <FormField label="Product categories" errorText={errors.productCategories?.message}>
              <TagInput value={field.value ?? []} onChange={field.onChange} />
            </FormField>
          )}
        />
        <Controller
          control={control}
          name="servicesOffered"
          render={({ field }) => (
            <FormField label="Services offered" errorText={errors.servicesOffered?.message}>
              <TagInput value={field.value ?? []} onChange={field.onChange} />
            </FormField>
          )}
        />
        <Controller
          control={control}
          name="deliveryTimeline"
          render={({ field }) => (
            <FormField label="Delivery timeline" errorText={errors.deliveryTimeline?.message}>
              <Input
                value={field.value ?? ''}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="Same-day, 2-3 business days, etc."
              />
            </FormField>
          )}
        />
        <Controller
          control={control}
          name="acceptedInsurance"
          render={({ field }) => (
            <FormField label="Accepted insurance" errorText={errors.acceptedInsurance?.message}>
              <TagInput value={field.value ?? []} onChange={field.onChange} />
            </FormField>
          )}
        />
        <Controller
          control={control}
          name="ehrIntegrations"
          render={({ field }) => (
            <FormField label="EHR integrations" errorText={errors.ehrIntegrations?.message}>
              <TagInput value={field.value ?? []} onChange={field.onChange} />
            </FormField>
          )}
        />
      </SpaceBetween>
    </Container>
  )

  const toggleContentType = (type: string, checked: boolean) => {
    const current = getValues('selectedContentTypes') ?? []
    const next = checked
      ? Array.from(new Set([...current, type]))
      : current.filter(t => t !== type)
    form.setValue('selectedContentTypes', next, { shouldValidate: true })
  }

  const step4Content = (
    <SpaceBetween size="l">
      {recommendError && (
        <Flashbar items={[{ type: 'error', content: recommendError, dismissible: true, onDismiss: () => setRecommendError(null) }]} />
      )}
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                iconName="gen-ai"
                loading={recommending}
                disabled={recommending}
                onClick={() => { void handleRecommendSettings() }}
              >
                Suggest with AI
              </Button>
            }
          >
            Campaign setup
          </Header>
        }
      >
        <SpaceBetween size="l">
          <Controller
            control={control}
            name="campaignGoal"
            render={({ field }) => (
              <FormField
                label="Campaign goal"
                description="What is the primary outcome you want from your campaigns?"
                errorText={errors.campaignGoal?.message}
              >
                <Textarea
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  rows={3}
                />
              </FormField>
            )}
          />

          <FormField
            label="Content types"
            description="Choose the content types this client should produce."
            errorText={errors.selectedContentTypes?.message}
          >
            <SpaceBetween size="xs">
              {ALL_CONTENT_TYPES.map(type => (
                <Checkbox
                  key={type}
                  checked={selectedContentTypes.includes(type)}
                  onChange={({ detail }) => toggleContentType(type, detail.checked)}
                >
                  {CONTENT_TYPE_LABELS[type] ?? type}
                </Checkbox>
              ))}
            </SpaceBetween>
          </FormField>

          <Controller
            control={control}
            name="budgetLimitDailyUsd"
            render={({ field }) => (
              <FormField
                label="Daily Budget Cap (USD)"
                description="Maximum spend per day in USD. Leave blank for no limit."
                errorText={errors.budgetLimitDailyUsd?.message}
              >
                <Input
                  type="number"
                  value={field.value != null ? String(field.value) : ''}
                  onChange={({ detail }) => {
                    const parsed = parseFloat(detail.value)
                    field.onChange(detail.value === '' ? undefined : isNaN(parsed) ? undefined : parsed)
                  }}
                  placeholder="e.g. 50"
                  inputMode="decimal"
                />
              </FormField>
            )}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Content inclusion</Header>}>
        <SpaceBetween size="xs">
          <Controller
            control={control}
            name="includeAddress"
            render={({ field }) => (
              <Checkbox checked={!!field.value} onChange={({ detail }) => field.onChange(detail.checked)}>
                Include business address
              </Checkbox>
            )}
          />
          <Controller
            control={control}
            name="includePhone"
            render={({ field }) => (
              <Checkbox checked={!!field.value} onChange={({ detail }) => field.onChange(detail.checked)}>
                Include phone number
              </Checkbox>
            )}
          />
          <Controller
            control={control}
            name="includeEmail"
            render={({ field }) => (
              <Checkbox checked={!!field.value} onChange={({ detail }) => field.onChange(detail.checked)}>
                Include email address
              </Checkbox>
            )}
          />
          <Controller
            control={control}
            name="includeWebsite"
            render={({ field }) => (
              <Checkbox checked={!!field.value} onChange={({ detail }) => field.onChange(detail.checked)}>
                Include website URL
              </Checkbox>
            )}
          />
          <Controller
            control={control}
            name="includePricing"
            render={({ field }) => (
              <Checkbox checked={!!field.value} onChange={({ detail }) => field.onChange(detail.checked)}>
                Include pricing
              </Checkbox>
            )}
          />
          <Controller
            control={control}
            name="includeStaffNames"
            render={({ field }) => (
              <Checkbox checked={!!field.value} onChange={({ detail }) => field.onChange(detail.checked)}>
                Include staff names
              </Checkbox>
            )}
          />
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  )

  const scheduleCadence = watch('scheduleCadence')
  const scheduleEnabled = watch('scheduleEnabled') ?? false

  const step5Content = (
    <Container
      header={
        <Header
          variant="h2"
          description="Automatically generate social content on a recurring schedule."
        >
          Auto-generation schedule
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Controller
          control={control}
          name="scheduleEnabled"
          render={({ field }) => (
            <Toggle
              checked={field.value ?? false}
              onChange={({ detail }) => field.onChange(detail.checked)}
            >
              Enable auto-generate
            </Toggle>
          )}
        />

        <Controller
          control={control}
          name="scheduleContentTypes"
          render={({ field }) => (
            <FormField
              label="Content types"
              description="Auto-generate is limited to Facebook and LinkedIn posts."
              errorText={errors.scheduleContentTypes?.message}
            >
              <Multiselect
                options={SCHEDULE_CONTENT_TYPE_OPTIONS}
                selectedOptions={(field.value ?? []).map(v =>
                  SCHEDULE_CONTENT_TYPE_OPTIONS.find(o => o.value === v) ?? { label: v, value: v },
                )}
                onChange={({ detail }) =>
                  field.onChange(
                    detail.selectedOptions
                      .map(o => o.value)
                      .filter((v): v is string => typeof v === 'string'),
                  )
                }
                placeholder="Choose content types"
                disabled={!scheduleEnabled}
              />
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="scheduleCadence"
          render={({ field }) => (
            <FormField label="Cadence" errorText={errors.scheduleCadence?.message}>
              <Select
                options={CADENCE_OPTIONS}
                selectedOption={CADENCE_OPTIONS.find(o => o.value === field.value) ?? null}
                onChange={({ detail }) =>
                  field.onChange((detail.selectedOption.value ?? 'daily') as ScheduleCadence)
                }
                placeholder="Choose a cadence"
                disabled={!scheduleEnabled}
              />
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="scheduleTimeUtc"
          render={({ field }) => (
            <FormField
              label="Time"
              description="Local time in the selected timezone"
              errorText={errors.scheduleTimeUtc?.message}
            >
              <Input
                value={field.value ?? ''}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="HH:MM"
                disabled={!scheduleEnabled}
              />
            </FormField>
          )}
        />

        {scheduleCadence === 'weekly' && (
          <Controller
            control={control}
            name="scheduleDaysOfWeek"
            render={({ field }) => (
              <FormField
                label="Days of week"
                description="Sunday = 0 through Saturday = 6"
                errorText={errors.scheduleDaysOfWeek?.message}
              >
                <Multiselect
                  options={DAY_OPTIONS}
                  selectedOptions={(field.value ?? []).map(v =>
                    DAY_OPTIONS.find(o => o.value === String(v)) ?? {
                      label: String(v),
                      value: String(v),
                    },
                  )}
                  onChange={({ detail }) =>
                    field.onChange(
                      detail.selectedOptions
                        .map(o => (o.value != null ? Number(o.value) : null))
                        .filter((n): n is number => n !== null && Number.isFinite(n)),
                    )
                  }
                  placeholder="Choose days"
                  disabled={!scheduleEnabled}
                />
              </FormField>
            )}
          />
        )}

        {scheduleCadence === 'custom_cron' && (
          <Controller
            control={control}
            name="scheduleCronExpr"
            render={({ field }) => (
              <FormField
                label="Cron expression"
                description="Standard 5-field cron expression interpreted in the selected timezone"
                errorText={errors.scheduleCronExpr?.message}
              >
                <Input
                  value={field.value ?? ''}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  placeholder="0 9 * * 1-5"
                  disabled={!scheduleEnabled}
                />
              </FormField>
            )}
          />
        )}

        <Controller
          control={control}
          name="scheduleTimezone"
          render={({ field }) => (
            <FormField
              label="Timezone"
              description="Schedule fires at the chosen time in this timezone. Defaults to your browser timezone."
              errorText={errors.scheduleTimezone?.message}
            >
              <Input
                value={field.value ?? ''}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="America/New_York"
                disabled={!scheduleEnabled}
              />
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="scheduleNotifyOnQueue"
          render={({ field }) => (
            <Toggle
              checked={field.value ?? false}
              onChange={({ detail }) => field.onChange(detail.checked)}
              disabled={!scheduleEnabled}
            >
              Send WhatsApp/Telegram notification when new items are queued
            </Toggle>
          )}
        />
      </SpaceBetween>
    </Container>
  )

  const steps: WizardProps.Step[] = [
    { title: 'Business Info', content: step1Content },
    { title: 'Online Presence', content: step2Content },
    { title: 'Services & Insurance', content: step3Content },
    { title: 'Campaign Setup', content: step4Content },
    { title: 'Auto-generation schedule', content: step5Content },
  ]

  return (
    <SpaceBetween size="l">
      <Wizard
        activeStepIndex={activeStep}
        steps={steps}
        isLoadingNextStep={saving}
        onNavigate={handleNavigate}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitButtonText="Finish setup"
        i18nStrings={{
          stepNumberLabel: n => `Step ${n}`,
          collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
          cancelButton: 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          optional: 'optional',
        }}
      />
    </SpaceBetween>
  )
}
